import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// ErrorBoundary — the durable backstop against render-time throws.
//
// A throw during render (e.g. `useMemo` calling `.toLowerCase()` on a null
// OCR'd player name) unmounts the ENTIRE React tree with no error boundary
// in the way — the user sees a blank white page and no explanation. React
// error boundaries only catch renders in their *children*, and can only be
// class components (no hook equivalent yet), so this wraps a whole page/view
// rather than trying to patch every individual render site.
//
// This is the class of fix, not the instance: the null-name crash sites
// upstream (lib/consensus.ts, MatchCardDemo.tsx) are guarded directly so the
// KNOWN cause never reaches here at all. This boundary exists for the next
// one nobody's found yet — swap "silent white screen" for "visible message
// + a way to recover" for ANY future render throw in the wrapped subtree.
// ─────────────────────────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Shown in the fallback heading, e.g. "this match card" or "the OCR Lab". */
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary]${this.props.label ? ` (${this.props.label})` : ''}`, error, info.componentStack);
  }

  // Lets a "try again" click re-attempt rendering the subtree without a full
  // page reload — useful when the throw was transient (e.g. a bad cache
  // read). If the underlying data is still broken it'll just re-catch.
  private retry = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-4 py-16 text-center">
          <AlertTriangle size={32} className="text-red-400" />
          <div className="max-w-md">
            <h2 className="font-display text-lg font-black uppercase tracking-tight text-white">
              Something went wrong rendering {this.props.label ?? 'this page'}
            </h2>
            <p className="mt-2 font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-iron-500">
              {error.message || 'An unexpected error occurred.'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={this.retry}
              className="flex items-center gap-2 rounded-md border border-iron-700 bg-iron-900 px-4 py-2 font-mono text-xs uppercase tracking-wider text-iron-200 hover:bg-iron-800"
            >
              <RefreshCw size={14} /> Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 rounded-md border border-iron-700 bg-iron-900 px-4 py-2 font-mono text-xs uppercase tracking-wider text-iron-200 hover:bg-iron-800"
            >
              <RefreshCw size={14} /> Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
